/**
 * Workstation load (PLAN G1 / V21). Two shapes leave the machine:
 *  - `LoadNumbers`: numbers and one boolean only, the plaintext scheduling metadata the hub keeps
 *    (same rule as the numeric `machine.status`: no labels, no text, no ids beyond routing ids);
 *  - `MachineLoadFrame` (`machine.load`): the encrypted report with history, per-run usage and
 *    the capacity recommendation. Only the account's own devices can read it.
 * The pure assess/bucket/recommend logic lives here so the daemon, the scheduler and the browser
 * all agree on what "overloaded" means without a shared dependency.
 */
import { z } from 'zod';
const Ts = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
const Pct = z.number().min(0).max(100);
/** 0 nominal, 1 fair, 2 serious, 3 critical (macOS thermal levels; temperature bands elsewhere). */
export const ThermalLevel = z.number().int().min(0).max(3);
export const LOAD_RESOURCES = ['cpu', 'mem', 'gpu', 'io', 'thermal'];
export const LoadNumbers = z.strictObject({
    cpuPct: Pct,
    memPct: Pct,
    gpuPct: Pct.optional(),
    ioPct: Pct.optional(),
    thermal: ThermalLevel.optional(),
    overloaded: z.boolean(),
});
export const LoadSample = z.strictObject({
    at: Ts,
    cpuPct: Pct,
    memPct: Pct,
    gpuPct: Pct.optional(),
    ioPct: Pct.optional(),
    /** Disk throughput, MB/s (where no busy-percent exists). */
    ioMBps: z.number().min(0).max(1_000_000).optional(),
    thermal: ThermalLevel.optional(),
});
export const LOAD_BUCKET_MS = 15 * 60_000;
export const LOAD_HISTORY_MS = 7 * 24 * 3_600_000;
/** One 15-minute bucket: averages, peaks and how many of its samples were over threshold. */
export const LoadBucket = z.strictObject({
    /** Bucket start (multiple of LOAD_BUCKET_MS). */
    at: Ts,
    n: z.number().int().positive().max(100_000),
    /** Samples in this bucket that were over a threshold (any resource). */
    over: z.number().int().nonnegative().max(100_000),
    cpuAvg: Pct,
    cpuMax: Pct,
    memAvg: Pct,
    memMax: Pct,
    gpuMax: Pct.optional(),
    ioMax: Pct.optional(),
    thermalMax: ThermalLevel.optional(),
    /** Which resources crossed a threshold in this bucket (for drill-down). */
    overBy: z.array(z.enum(LOAD_RESOURCES)).max(5),
});
export const RunUsage = z.strictObject({
    /** Run id; rotated runs carry a `.rN` suffix (`nextRotationId`). */
    runId: z.string().regex(/^[A-Za-z0-9._:-]{1,128}$/),
    /** Percent of one core, summed over the run's process tree (may exceed 100). */
    cpuPct: z.number().min(0).max(100_000).optional(),
    rssMb: z.number().min(0).max(10_000_000),
    procs: z.number().int().nonnegative().max(100_000),
});
export const LoadThresholds = z.strictObject({
    cpuPct: Pct,
    memPct: Pct,
    gpuPct: Pct,
    ioPct: Pct,
    /** Overloaded at or above this thermal level. */
    thermal: ThermalLevel,
    /** A resource must fall this many points below its threshold to clear (hysteresis). */
    clearMargin: z.number().min(0).max(50),
    /** Consecutive over-threshold samples before flipping to overloaded. */
    confirmSamples: z.number().int().min(1).max(20),
});
export const DEFAULT_LOAD_THRESHOLDS = {
    cpuPct: 90,
    memPct: 90,
    gpuPct: 95,
    ioPct: 90,
    thermal: 2,
    clearMargin: 10,
    confirmSamples: 2,
};
export const CapacityRecommendation = z.strictObject({
    kind: z.enum(['upgrade', 'rebalance']),
    resource: z.enum(LOAD_RESOURCES),
    /** Evidence, all numbers: what the text is derived from. */
    overPct: Pct,
    peak: z.number().min(0).max(100),
    avg: z.number().min(0).max(100),
    buckets: z.number().int().nonnegative(),
    windowHours: z.number().nonnegative(),
    text: z.string().max(400),
});
export const MachineLoadFrame = z.strictObject({
    type: z.literal('machine.load'),
    ts: Ts,
    sample: LoadSample,
    thresholds: LoadThresholds,
    overloaded: z.boolean(),
    /** Why, when overloaded (e.g. "CPU 96% ≥ 90%"). */
    reasons: z.array(z.string().max(120)).max(5),
    overloadedSince: Ts.optional(),
    /** Last ~10 minutes at sample cadence (sparkline). */
    recent: z.array(LoadSample).max(120),
    /** Up to a week of 15-minute buckets, oldest first. */
    history: z.array(LoadBucket).max(700),
    runs: z.array(RunUsage).max(64),
    recommendations: z.array(CapacityRecommendation).max(5),
});
const NAMES = {
    cpu: 'CPU',
    mem: 'Memory',
    gpu: 'GPU',
    io: 'Disk',
    thermal: 'Thermal',
};
export const loadResourceName = (r) => NAMES[r];
function valueOfRes(r, res) {
    return { cpu: r.cpuPct, mem: r.memPct, gpu: r.gpuPct, io: r.ioPct, thermal: r.thermal }[res];
}
function limitOf(t, res) {
    return { cpu: t.cpuPct, mem: t.memPct, gpu: t.gpuPct, io: t.ioPct, thermal: t.thermal }[res];
}
/** Resources over their threshold (`margin` lowers the bar: used to hold an overload). */
export function overResources(r, t, margin = 0) {
    return LOAD_RESOURCES.filter((res) => {
        const v = valueOfRes(r, res);
        if (v === undefined)
            return false;
        return res === 'thermal'
            ? v >= Math.max(1, limitOf(t, res) - (margin > 0 ? 1 : 0))
            : v >= limitOf(t, res) - margin;
    });
}
export function describeOver(r, t, res) {
    return res.map((x) => {
        const v = valueOfRes(r, x);
        return x === 'thermal'
            ? `Thermal level ${v} ≥ ${limitOf(t, x)}`
            : `${NAMES[x]} ${Math.round(v ?? 0)}% ≥ ${limitOf(t, x)}%`;
    });
}
export const INITIAL_LOAD_STATE = { overloaded: false, streak: 0 };
/**
 * Advance the overload state machine by one sample. Overload needs `confirmSamples` consecutive
 * over-threshold samples (spikes do not count) and clears only once every resource is
 * `clearMargin` below its limit (no flapping around the threshold).
 */
export function stepLoad(state, sample, t) {
    if (state.overloaded) {
        const held = overResources(sample, t, t.clearMargin);
        if (held.length === 0)
            return { state: { overloaded: false, streak: 0 }, reasons: [] };
        return { state, reasons: describeOver(sample, t, held) };
    }
    const over = overResources(sample, t);
    if (over.length === 0)
        return { state: { overloaded: false, streak: 0 }, reasons: [] };
    const streak = state.streak + 1;
    if (streak >= t.confirmSamples)
        return {
            state: { overloaded: true, since: sample.at, streak },
            reasons: describeOver(sample, t, over),
        };
    return { state: { overloaded: false, streak }, reasons: [] };
}
export function toLoadNumbers(s, overloaded) {
    return {
        cpuPct: round1(s.cpuPct),
        memPct: round1(s.memPct),
        ...(s.gpuPct !== undefined ? { gpuPct: round1(s.gpuPct) } : {}),
        ...(s.ioPct !== undefined ? { ioPct: round1(s.ioPct) } : {}),
        ...(s.thermal !== undefined ? { thermal: s.thermal } : {}),
        overloaded,
    };
}
const round1 = (n) => Math.round(Math.min(100, Math.max(0, n)) * 10) / 10;
/** Fold a sample into the (sorted, oldest-first) bucket list; prunes past a week. Pure. */
export function foldSample(history, s, t) {
    const at = Math.floor(s.at / LOAD_BUCKET_MS) * LOAD_BUCKET_MS;
    const over = overResources(s, t);
    const out = history.filter((b) => b.at > s.at - LOAD_HISTORY_MS);
    const last = out[out.length - 1];
    const max = (a, b) => a === undefined ? b : b === undefined ? a : Math.max(a, b);
    if (last && last.at === at) {
        const n = last.n + 1;
        const overBy = [...new Set([...last.overBy, ...over])];
        out[out.length - 1] = {
            at,
            n,
            over: last.over + (over.length ? 1 : 0),
            cpuAvg: round1((last.cpuAvg * last.n + s.cpuPct) / n),
            cpuMax: round1(Math.max(last.cpuMax, s.cpuPct)),
            memAvg: round1((last.memAvg * last.n + s.memPct) / n),
            memMax: round1(Math.max(last.memMax, s.memPct)),
            ...opt('gpuMax', max(last.gpuMax, s.gpuPct)),
            ...opt('ioMax', max(last.ioMax, s.ioPct)),
            ...opt('thermalMax', max(last.thermalMax, s.thermal)),
            overBy,
        };
    }
    else {
        out.push({
            at,
            n: 1,
            over: over.length ? 1 : 0,
            cpuAvg: round1(s.cpuPct),
            cpuMax: round1(s.cpuPct),
            memAvg: round1(s.memPct),
            memMax: round1(s.memPct),
            ...opt('gpuMax', s.gpuPct),
            ...opt('ioMax', s.ioPct),
            ...opt('thermalMax', s.thermal),
            overBy: over,
        });
    }
    return out;
}
function opt(k, v) {
    return v === undefined ? {} : { [k]: v };
}
/**
 * Sustained overload over the history window yields a recommendation with the evidence it was
 * derived from. One machine at a time; one recommendation per over-used resource.
 */
export function recommendCapacity(history, t, o = {}) {
    const minBuckets = o.minBuckets ?? 24;
    const share = o.sustainedShare ?? 0.2;
    if (history.length < minBuckets)
        return [];
    const hours = round1(history.length * (LOAD_BUCKET_MS / 3_600_000));
    const out = [];
    for (const res of LOAD_RESOURCES) {
        const overBuckets = history.filter((b) => b.overBy.includes(res)).length;
        const overShare = overBuckets / history.length;
        if (overShare < share)
            continue;
        const peaks = history.map((b) => peakOf(b, res)).filter((v) => v !== undefined);
        const avgs = history.map((b) => avgOf(b, res)).filter((v) => v !== undefined);
        if (!peaks.length)
            continue;
        const peak = round1(Math.max(...peaks));
        const avg = round1(avgs.reduce((a, b) => a + b, 0) / Math.max(1, avgs.length));
        const kind = o.peersHaveHeadroom ? 'rebalance' : 'upgrade';
        const overPct = Math.round(overShare * 100);
        const what = res === 'thermal' ? 'thermal pressure' : `${NAMES[res]} load`;
        out.push({
            kind,
            resource: res,
            overPct,
            peak,
            avg,
            buckets: history.length,
            windowHours: hours,
            text: kind === 'rebalance'
                ? `${NAMES[res]} was over its limit for ${overPct}% of the last ${Math.round(hours)}h. Other machines have headroom: shift more work there.`
                : `Sustained ${what}: over the limit for ${overPct}% of the last ${Math.round(hours)}h (peak ${peak}${res === 'thermal' ? '' : '%'}). Consider a more capable machine or lowering this machine's run cap.`,
        });
    }
    return out;
}
function peakOf(b, r) {
    return { cpu: b.cpuMax, mem: b.memMax, gpu: b.gpuMax, io: b.ioMax, thermal: b.thermalMax }[r];
}
function avgOf(b, r) {
    return { cpu: b.cpuAvg, mem: b.memAvg, gpu: b.gpuMax, io: b.ioMax, thermal: b.thermalMax }[r];
}
//# sourceMappingURL=load.js.map